import {Button, Hero, useTheme, useWindowSize, type HeadingProps} from '@primer/react-brand/esm'
import {clsx} from 'clsx'
import {useId, useMemo, useRef, useState, type SyntheticEvent} from 'react'

import {OSAwareDownloadActionMenu} from '@github-ui/swp-core/components/OSAwareDownloadActionMenu'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'
import {t} from '@github-ui/swp-core/lib/utils/localization'

import {PlayButton, type PlayButtonProps} from '../../../../../brand/components/PlayButton/PlayButton'
import type {Cta, DownloadMenu, ThemedVisual} from '../../../Index.types'
import {Product, type ProductProps} from '../Product/Product'

import Styles from './Showcase.module.css'

export type ShowcasePrimaryCta = (Cta & {type: 'link'}) | (DownloadMenu & {type: 'download'})

export interface ShowcaseProps {
  title: string
  description: string
  cta1: ShowcasePrimaryCta
  cta2: Cta
  visual: ThemedVisual & {type: 'image' | 'video'}
}

export const defaultProps: Partial<ShowcaseProps> = {}

// Hero.Heading forwards these props but omits `as` from its public type.
const HEADING_PROPS = {as: 'h2', size: '3', weight: 'medium', textWrap: 'balance'} satisfies HeadingProps
const BACKGROUND_PROPS = {hasCornerDots: true}
const CTA_ANALYTICS = {context: 'CTAs', location: 'showcase', tag: 'a'}
const PLAYBACK_LABELS = {
  play: t('Play video'),
  pause: t('Pause video'),
  replay: t('Replay video'),
}
const PRODUCT_UI = {
  shadowColor: 'blueDark',
  shadowPosition: 'left-bottom',
  roundedCorners: {topRight: false, bottomRight: false},
} satisfies ProductProps['productUI']
const BOTTOM_PRODUCT_UI = {
  ...PRODUCT_UI,
  shadowPosition: 'left',
  roundedCorners: {bottomLeft: false, bottomRight: false},
} satisfies ProductProps['productUI']

export const Showcase = (props: ShowcaseProps) => {
  const initializedProps: ShowcaseProps = {...defaultProps, ...props}
  const {title, description, cta1, cta2, visual} = initializedProps
  const titleId = useId()
  const {colorMode} = useTheme()
  const {isSmall: isTablet} = useWindowSize()
  const mediaUrl = colorMode === 'dark' ? visual.url.dark || visual.url.light : visual.url.light
  const actions = props.cta1.type === 'download' ? props.cta1.actions : undefined
  const downloadActions = useMemo(() => actions?.map(({url, ...action}) => ({...action, href: url})) ?? [], [actions])
  const videoRef = useRef<HTMLVideoElement>(null)
  const [playbackAction, setPlaybackAction] = useState<PlayButtonProps['action']>('play')

  const updatePlayback = (event: SyntheticEvent<HTMLVideoElement>) => {
    const video = event.currentTarget
    setPlaybackAction(video.ended ? 'replay' : video.paused ? 'play' : 'pause')
  }

  const togglePlayback = async () => {
    const video = videoRef.current
    if (!video) return

    if (!video.paused && !video.ended) {
      video.pause()
      return
    }

    if (video.ended) video.currentTime = 0
    try {
      await video.play()
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return
      window.reportError(error)
    }
  }

  return (
    <section className={Styles.Showcase} aria-labelledby={titleId}>
      <Product
        backgroundProps={BACKGROUND_PROPS}
        mediaPosition={isTablet ? 'bottom-center' : 'center-left'}
        minPadding="var(--showcaseMediaPadding)"
        theme={colorMode}
        productUI={isTablet ? BOTTOM_PRODUCT_UI : PRODUCT_UI}
        firstElementClass={Styles.Showcase__firstElement}
        firstElement={
          <div className={Styles.Showcase__header}>
            <Hero align="center" className={Styles.Showcase__hero} aria-labelledby={undefined}>
              <Hero.Heading {...HEADING_PROPS} id={titleId}>
                {title}
              </Hero.Heading>
              <Hero.Description size="300" className={Styles.Showcase__description}>
                {description}
              </Hero.Description>
            </Hero>
            <div className={Styles.Showcase__actions}>
              {cta1.type === 'download' ? (
                <div className={Styles.Showcase__downloadMenu}>
                  <OSAwareDownloadActionMenu
                    actions={downloadActions}
                    menuLabel={cta1.menuLabel}
                    analytics={CTA_ANALYTICS}
                    variant="primary"
                  />
                </div>
              ) : (
                <Button
                  className={Styles.Showcase__action}
                  as="a"
                  href={cta1.url}
                  variant="primary"
                  {...getAnalyticsEvent({...CTA_ANALYTICS, action: cta1.label})}
                >
                  {cta1.label}
                </Button>
              )}
              <Button
                className={Styles.Showcase__action}
                as="a"
                href={cta2.url}
                variant="secondary"
                {...getAnalyticsEvent({...CTA_ANALYTICS, action: cta2.label})}
              >
                {cta2.label}
              </Button>
            </div>
          </div>
        }
      >
        {mediaUrl &&
          (visual.type === 'video' ? (
            <div className={Styles.Showcase__videoContainer}>
              <video
                ref={videoRef}
                className={clsx(Styles.Showcase__media, Styles.Showcase__video)}
                src={mediaUrl}
                aria-label={visual.alt}
                muted
                playsInline
                preload="metadata"
                onPlay={updatePlayback}
                onPause={updatePlayback}
                onEnded={updatePlayback}
                onEmptied={updatePlayback}
                onLoadedMetadata={updatePlayback}
              />
              <div
                className={Styles.Showcase__playback}
                {...getAnalyticsEvent({
                  context: 'video',
                  location: 'showcase',
                  tag: 'button',
                  action: playbackAction,
                })}
              >
                <PlayButton action={playbackAction} label={PLAYBACK_LABELS[playbackAction]} onClick={togglePlayback} />
              </div>
            </div>
          ) : (
            <img className={Styles.Showcase__media} src={mediaUrl} alt={visual.alt} loading="lazy" />
          ))}
      </Product>
    </section>
  )
}
